import * as React from 'react';

import { SummaryRow } from '../../../Summary/SummaryRow';
import { DocAccessType, IDocumentsList, IRisk } from '../../../../types';
import { Section } from '../../../Finger/Section';
import FingerWithApprove from '../../../atoms/finger-with-approve';
import NoDocuments from '../../../atoms/no-doc-placeholder';
import { isFundingRisk } from '../../../../utilities/risks';
import { getFingerStatus, getVisible, hasDeleted } from '../../../../utilities/documents';

import { UserRole } from '../../../../store/auth/types';
import { EvidenceOfIncomeDocs } from 'types';
import { SummaryGroup } from '../../../Summary/SummaryGroup';
import { useTranslation } from "react-i18next";

interface IProps {
  infoOnly: boolean;
  userAccess?: DocAccessType;
  id: string;
  leadSponsor: string;
  documents: EvidenceOfIncomeDocs;
  docRisk: IRisk['financeDocsRisk']['evidenceOfIncome'];
  isDeleting: boolean;
  userRole?: UserRole;

  canCheckOff?: boolean;
  canResume: boolean;
  canUpload: boolean;
  canOpen: boolean;
  canApprove: boolean;
  canReject: boolean;
  canAcknowledge: boolean;
  showRisks: boolean;

  isCheckedOffDocRisks?: (docType: string, docKey: string) => boolean;
  isCheckedOffFinanceRisks?: (docType: string, docKey: string) => boolean;
  onCheckOffDocRisks?: (docType: string, docKey: string, docTitle: string) => void;
  onCheckOffFinanceRisks?: (docType: string, docKey: string, docTitle: string) => void;
  onResume: (url: string) => void;
  onUploadClick: (sponsorType: string, docType: string | null, docKey: string, modalTitle: string) => void;
  onOpenClick: (sponsorType: string, docType: string | null, docKey: string) => void;
  onApproveClick: (sponsorType: string, docType: string | null, docKey: string) => void;
  onRejectClick: (sponsorType: string, docType: string | null, docKey: string, modalTitle: string) => void;
  onRescindRejectClick: (sponsorType: string, docType: string | null, docKey: string, modalTitle: string) => void;
  onDeleteClick: (sponsorType: string, docType: string | null, docKey: string, modalTitle: string) => void;
  onAcknowledgeClick: (sponsorType: string, docType: string | null, docKey: string) => void;
}

const EvidenceOfIncomeSection = (props: IProps) => {
    const { t } = useTranslation('common');
  const { showRisks, leadSponsor = 'Student', infoOnly, userAccess, userRole } = props;

  const dispLeadSponsor = leadSponsor === 'Student' ? 'My' : `${leadSponsor}'s`;

  const evidenceOfIncomeDocs = props.documents;

  const currentEmploymentStatus = (
    Object.values(evidenceOfIncomeDocs || {})
      .flatMap(doc => Object.values(doc))
      .find(item => (item as EvidenceOfIncomeDocs).currentEmploymentStatus) as EvidenceOfIncomeDocs
  )?.currentEmploymentStatus;

  const fundSource = (
    Object.values(evidenceOfIncomeDocs || {})
      .flatMap(doc => Object.values(doc))
      .find(item => (item as EvidenceOfIncomeDocs).fundSource) as EvidenceOfIncomeDocs
  )?.fundSource;
  const getMetaData = () => {
    return (
      <SummaryGroup
        title={t('common:leadSponsor', { leadSponsor })}
        key={`fin_doc_section_${props.id}`}
        boxProps={{ as: 'div', pt: 0 }}
      >
        <SummaryRow
          key={`lead_sponsor_employment_status_${props.id}`}
          label={t('common:currentEmploymentStatus')}
          fullLabel={`What is your ${leadSponsor !== 'Student' ? `${leadSponsor}'s ` : ''}current employment status?`}
          value={currentEmploymentStatus}
          isRisk={showRisks && isFundingRisk(props.docRisk, 'currentEmploymentStatus', leadSponsor)}
        />

        {fundSource ? (
          <>
            <SummaryRow
              key={`lead_sponsor_funds_source_${props.id}`}
              label={t('common:fundsSource')}
              fullLabel={`Please explain how the funds for your study came to be in your ${
                leadSponsor !== 'student' ? `${leadSponsor}'s ` : ''
              }account despite no recent employment history`}
              value={fundSource}
              isRisk={showRisks && false}
            />
          </>
        ) : null}
      </SummaryGroup>
    );
  };

  let hasDocs = false;
  let hasDeletedDocs = false;
  const docNodes: React.ReactNode[] = [];
  const { curriculumVitae } = evidenceOfIncomeDocs;
  const { employmentReference } = evidenceOfIncomeDocs;
  const { recentEmploymentContract } = evidenceOfIncomeDocs;
  const { recentPayslip } = evidenceOfIncomeDocs;

  if (curriculumVitae) {
    const visibleDocs = getVisible({
      documentsHash: curriculumVitae,
      userAccess,
      infoOnly,
      userRole,
    }) as IDocumentsList[];
    if (hasDeleted(curriculumVitae)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      const modalTitle = `${dispLeadSponsor} CV / Resume`;
      docNodes.push(
        ...visibleDocs.map((doc: IDocumentsList, index: number) => {
          const { key, item } = doc;

          return (
            <FingerWithApprove
              id={`curriculum-vitae-${key}`}
              key={`curriculum-vitae-${key}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={t('common:text5', { modalTitle, index: Object.keys(curriculumVitae).length > 1 ? (index > 0 ? ` ${index + 1}` : '') : '' })}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              canCheckOff={props.canCheckOff}
              isCheckedOff={() =>
                props.isCheckedOffDocRisks ? props.isCheckedOffDocRisks('evidenceOfIncome', key) : false
              }
              onCheckOffRisk={() =>
                props.onCheckOffDocRisks && props.onCheckOffDocRisks('evidenceOfIncome', key, modalTitle)
              }
              onUploadClick={() => props.onUploadClick('evidenceOfIncome', 'curriculumVitae', key, modalTitle)}
              onOpenClick={() => props.onOpenClick('evidenceOfIncome', 'curriculumVitae', key)}
              onApproveClick={() => props.onApproveClick('evidenceOfIncome', 'curriculumVitae', key)}
              onRejectClick={() => {
                props.onRejectClick('evidenceOfIncome', 'curriculumVitae', key, modalTitle);
              }}
              onRescindRejectClick={() => {
                props.onRescindRejectClick('evidenceOfIncome', 'curriculumVitae', key, modalTitle);
              }}
              onDeleteClick={() => {
                props.onDeleteClick('evidenceOfIncome', 'curriculumVitae', key, modalTitle);
              }}
              onAcknowledgeClick={() => props.onAcknowledgeClick('evidenceOfIncome', 'curriculumVitae', key)}
              buttonLabel={'How to prove'}
              resolveLabel={'Requires attention'}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            />
          );
        }),
      );
    }
  }

  if (employmentReference) {
    const visibleDocs = getVisible({
      documentsHash: employmentReference,
      userAccess,
      infoOnly,
      userRole,
    }) as IDocumentsList[];
    if (hasDeleted(employmentReference)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      const modalTitle = `${dispLeadSponsor} Employment Reference`;
      docNodes.push(
        ...visibleDocs.map((doc: IDocumentsList, index: number) => {
          const { key, item } = doc;

          return (
            <FingerWithApprove
              id={`employment-reference-${key}`}
              key={`employment-reference-${key}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={t('common:text5', { modalTitle, index: Object.keys(employmentReference).length > 1 ? (index > 0 ? ` ${index + 1}` : '') : '' })}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              canCheckOff={props.canCheckOff}
              isCheckedOff={() =>
                props.isCheckedOffDocRisks ? props.isCheckedOffDocRisks('evidenceOfIncome', key) : false
              }
              onCheckOffRisk={() =>
                props.onCheckOffDocRisks && props.onCheckOffDocRisks('evidenceOfIncome', key, modalTitle)
              }
              onUploadClick={() => props.onUploadClick('evidenceOfIncome', 'employmentReference', key, modalTitle)}
              onOpenClick={() => props.onOpenClick('evidenceOfIncome', 'employmentReference', key)}
              onApproveClick={() => props.onApproveClick('evidenceOfIncome', 'employmentReference', key)}
              onRejectClick={() => {
                props.onRejectClick('evidenceOfIncome', 'employmentReference', key, modalTitle);
              }}
              onRescindRejectClick={() => {
                props.onRescindRejectClick('evidenceOfIncome', 'employmentReference', key, modalTitle);
              }}
              onDeleteClick={() => {
                props.onDeleteClick('evidenceOfIncome', 'employmentReference', key, modalTitle);
              }}
              onAcknowledgeClick={() => props.onAcknowledgeClick('evidenceOfIncome', 'employmentReference', key)}
              buttonLabel={'How to prove'}
              resolveLabel={'Requires attention'}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            />
          );
        }),
      );
    }
  }

  if (recentEmploymentContract) {
    const visibleDocs = getVisible({
      documentsHash: recentEmploymentContract,
      userAccess,
      infoOnly,
      userRole,
    }) as IDocumentsList[];
    if (hasDeleted(recentEmploymentContract)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      const modalTitle = `${dispLeadSponsor} Employment Contract`;
      docNodes.push(
        ...visibleDocs.map((doc: IDocumentsList, index: number) => {
          const { key, item } = doc;

          return (
            <FingerWithApprove
              id={`recent-employment-contract-${key}`}
              key={`recent-employment-contract-${key}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={t('common:text5', { modalTitle, index: Object.keys(recentEmploymentContract).length > 1 ? (index > 0 ? ` ${index + 1}` : '') : '' })}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              canCheckOff={props.canCheckOff}
              isCheckedOff={() =>
                props.isCheckedOffDocRisks ? props.isCheckedOffDocRisks('evidenceOfIncome', key) : false
              }
              onCheckOffRisk={() =>
                props.onCheckOffDocRisks && props.onCheckOffDocRisks('evidenceOfIncome', key, modalTitle)
              }
              onUploadClick={() => props.onUploadClick('evidenceOfIncome', 'recentEmploymentContract', key, modalTitle)}
              onOpenClick={() => props.onOpenClick('evidenceOfIncome', 'recentEmploymentContract', key)}
              onApproveClick={() => props.onApproveClick('evidenceOfIncome', 'recentEmploymentContract', key)}
              onRejectClick={() => {
                props.onRejectClick('evidenceOfIncome', 'recentEmploymentContract', key, modalTitle);
              }}
              onRescindRejectClick={() => {
                props.onRescindRejectClick('evidenceOfIncome', 'recentEmploymentContract', key, modalTitle);
              }}
              onDeleteClick={() => {
                props.onDeleteClick('evidenceOfIncome', 'recentEmploymentContract', key, modalTitle);
              }}
              onAcknowledgeClick={() => props.onAcknowledgeClick('evidenceOfIncome', 'recentEmploymentContract', key)}
              buttonLabel={'How to prove'}
              resolveLabel={'Requires attention'}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            />
          );
        }),
      );
    }
  }

  if (recentPayslip) {
    const visibleDocs = getVisible({
      documentsHash: recentPayslip,
      userAccess,
      infoOnly,
      userRole,
    }) as IDocumentsList[];
    if (hasDeleted(recentPayslip)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      const modalTitle = `${dispLeadSponsor} Employment Payslip`;
      docNodes.push(
        ...visibleDocs.map((doc: IDocumentsList, index: number) => {
          const { key, item } = doc;

          return (
            <FingerWithApprove
              id={`recent-payslip-${key}`}
              key={`recent-payslip-${key}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={t('common:text5', { modalTitle, index: Object.keys(recentPayslip).length > 1 ? (index > 0 ? ` ${index + 1}` : '') : '' })}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              canCheckOff={props.canCheckOff}
              isCheckedOff={() =>
                props.isCheckedOffDocRisks ? props.isCheckedOffDocRisks('evidenceOfIncome', key) : false
              }
              onCheckOffRisk={() =>
                props.onCheckOffDocRisks && props.onCheckOffDocRisks('evidenceOfIncome', key, modalTitle)
              }
              onUploadClick={() => props.onUploadClick('evidenceOfIncome', 'recentPayslip', key, modalTitle)}
              onOpenClick={() => props.onOpenClick('evidenceOfIncome', 'recentPayslip', key)}
              onApproveClick={() => props.onApproveClick('evidenceOfIncome', 'recentPayslip', key)}
              onRejectClick={() => {
                props.onRejectClick('evidenceOfIncome', 'recentPayslip', key, modalTitle);
              }}
              onRescindRejectClick={() => {
                props.onRescindRejectClick('evidenceOfIncome', 'recentPayslip', key, modalTitle);
              }}
              onDeleteClick={() => {
                props.onDeleteClick('evidenceOfIncome', 'recentPayslip', key, modalTitle);
              }}
              onAcknowledgeClick={() => props.onAcknowledgeClick('evidenceOfIncome', 'recentPayslip', key)}
              buttonLabel={'How to prove'}
              resolveLabel={'Requires attention'}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            />
          );
        }),
      );
    }
  }

  if (!hasDocs && !hasDeletedDocs) {
    return null;
  }

  if ((infoOnly || userAccess) && !hasDocs) {
    return null;
  }

  return (
    <Section
      id={`evidence-of-income-${evidenceOfIncomeDocs.leadSponsor!}`}
      label={t('common:evidenceOfIncomeFrom', { leadSponsor })}
      isRisk={showRisks && isFundingRisk(props.docRisk, 'currentEmploymentStatus', evidenceOfIncomeDocs.leadSponsor!)}
      isRiskSection={
        showRisks && isFundingRisk(props.docRisk, 'currentEmploymentStatus', evidenceOfIncomeDocs.leadSponsor!)
      }
      canCheckOff={props.canCheckOff}
      isCheckedOff={() =>
        props.isCheckedOffFinanceRisks
          ? props.isCheckedOffFinanceRisks('evidenceOfIncome', evidenceOfIncomeDocs.leadSponsor!)
          : false
      }
      onCheckOffRisk={() =>
        props.onCheckOffFinanceRisks &&
        props.onCheckOffFinanceRisks('evidenceOfIncome', evidenceOfIncomeDocs.leadSponsor!, 'Evidence Of Income')
      }
      canEdit={false}
      metaData={getMetaData()}
      amount="Details"
    >
      {hasDocs ? docNodes : <NoDocuments />}
    </Section>
  );
};

export default EvidenceOfIncomeSection;
